// Updated doctor modal with cleaned logs and English comments

// Import necessary functions
import { loadContentForNewElements } from '../content-editor-src';
import { setupEditButton } from '../content-editor-src/content-editor/element-setup';
import { isAuthenticated, getAuthToken } from '../auth';
import { updatePendingChanges, removePendingChange } from '../content-editor-src/content-store';
import { config } from '../../config/app-config';
import { apiPost } from '../../utils/api';

// Storage for doctor experience changes between sessions
const doctorExperienceCache = {};

// Use event delegation instead of direct handlers
export function initDoctorModals() {
    // Remove existing handlers if they exist
    document.removeEventListener('click', handleDoctorCardClick);

    // Add one handler to document with event delegation
    document.addEventListener('click', handleDoctorCardClick);
}

// Click handler with target element check
function handleDoctorCardClick(event) {
    // Check if click was on doctor card or inside it
    const doctorCard = findDoctorCard(event.target);

    if (doctorCard) {
        // Get doctor data - check both card types
        let doctorName, doctorDescription, doctorImage, doctorImageSrc;

        if (doctorCard.classList.contains('doctor-card-about')) {
            // For about page cards
            doctorName = doctorCard.querySelector('.doctor-name-about')?.textContent || 'Doctor';
            doctorDescription = doctorCard.querySelector('.doctor-description-about')?.textContent || '';
            doctorImage = doctorCard.querySelector('.doctor-image-about img');
        } else {
            // For regular cards
            doctorName = doctorCard.querySelector('.doctor-name')?.textContent || 'Doctor';
            doctorDescription = doctorCard.querySelector('.doctor-description')?.textContent || '';
            doctorImage = doctorCard.querySelector('.doctor-image img');
        }

        doctorImageSrc = doctorImage ? doctorImage.src : '';

        // Get block and its name for proper data-content binding
        const blockName = doctorCard.getAttribute('data-block-name') || '';

        // Get experience from cache or DOM element
        let experience = '';
        const experienceTagId = `${blockName}-experience`;

        if (doctorExperienceCache[experienceTagId]) {
            // If in cache - use from there
            experience = doctorExperienceCache[experienceTagId];
        } else {
            // Otherwise try to get from DOM
            const experienceElement = doctorCard.querySelector(`[data-content="${experienceTagId}"]`);
            if (experienceElement) {
                experience = experienceElement.textContent || '';
            }
        }

        openDoctorModal(doctorName, doctorDescription, doctorImageSrc, blockName, experience);
        event.preventDefault();
    }
}

// Function to find parent doctor card - updated to support both card types
function findDoctorCard(element) {
    while (element && !element.classList.contains('doctor-card') && !element.classList.contains('doctor-card-about')) {
        element = element.parentElement;
    }
    return element;
}

// Function to create modal window (with experience section added)
function openDoctorModal(name, description, imageSrc, blockName, experience) {
    const modal = document.createElement('div');
    modal.className = 'doctor-modal';
    modal.id = 'doctor-modal-' + blockName; // Add unique ID
    //language=HTML
    modal.innerHTML = `
        <div class="doctor-modal-content">
            <div class="doctor-modal-head">
                <div class="doctor-modal-image">
                    <img src="${imageSrc}" alt="${name}">
                </div>
                <div class="doctor-modal-info">
                    <h3>${name}</h3>
                    <p>${description}</p>
                </div>
                <div class="close-modal">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="30" height="30" rx="15" fill="#EBFBFF"/>
                        <rect x="9.25488" y="8.37109" width="17.5" height="1.25" rx="0.625"
                              transform="rotate(45 9.25488 8.37109)" fill="#009DC9"/>
                        <rect x="8.37061" y="20.7461" width="17.5" height="1.25" rx="0.625"
                              transform="rotate(-45 8.37061 20.7461)" fill="#009DC9"/>
                    </svg>
                </div>

            </div>

            <div class="experience">
                <h5>Досвід роботи</h5>
                <p data-content="${blockName}-experience" data-page="Home" data-type="textarea">${experience || ''}</p>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Find experience element and initialize content editor for it
    const experienceElement = modal.querySelector(`[data-content="${blockName}-experience"]`);

    if (experienceElement) {
        // Load content and make element editable
        loadContentForNewElements([experienceElement]);

        // If user is authenticated, add edit button
        if (isAuthenticated()) {
            setTimeout(() => {
                setupEditButton(experienceElement);

                // Add change handler for saving to cache
                const experienceWrapper = experienceElement.closest('.editable-wrapper');
                if (experienceWrapper) {
                    const editBtn = experienceWrapper.querySelector('.edit-btn');
                    if (editBtn) {
                        const originalClick = editBtn.onclick;
                        editBtn.onclick = function(e) {
                            // Call original handler
                            if (originalClick) originalClick.call(this, e);

                            // Add handler for saving to cache when closing edit modal
                            const tagId = `${blockName}-experience`;
                            const saveExperienceHandler = () => {
                                // Save content to cache before closing
                                if (experienceElement.textContent) {
                                    doctorExperienceCache[tagId] = experienceElement.textContent;

                                    // Find doctor card in main DOM and update hidden element
                                    const doctorCard = document.querySelector(`[data-block-name="${blockName}"]`);
                                    if (doctorCard) {
                                        const hiddenExperience = doctorCard.querySelector(`.doctor-experience[data-content="${tagId}"]`);
                                        if (hiddenExperience) {
                                            hiddenExperience.textContent = experienceElement.textContent;

                                            // Prepare changes for server save
                                            updatePendingChanges('text', tagId, {
                                                pageTitle: 'Home',
                                                tagId: tagId,
                                                content: experienceElement.textContent,
                                                blockName: blockName,
                                                dataAttribute: 'textarea'
                                            });
                                        } else {
                                            console.warn(`Hidden element with data-content="${tagId}" not found`);
                                        }
                                    } else {
                                        console.warn(`Doctor card with data-block-name="${blockName}" not found`);
                                    }
                                }
                            };

                            // For proper context support inside editor modal
                            window.currentDoctorExperienceTagId = tagId;

                            // Add handler to modal close button
                            const closeBtn = modal.querySelector('.close-modal');
                            if (closeBtn) {
                                closeBtn.addEventListener('click', saveExperienceHandler, { once: true });
                            }

                            // Also add handler for clicking outside modal
                            modal.addEventListener('click', function(evt) {
                                if (evt.target === modal) {
                                    saveExperienceHandler();
                                }
                            }, { once: true });
                        };
                    }
                }
            }, 200); // Increase delay for reliability
        }
    }

    const closeButton = modal.querySelector('.close-modal');
    closeButton.addEventListener('click', function() {
        document.body.removeChild(modal);
    });

    modal.addEventListener('click', function(event) {
        if (event.target === modal) {
            document.body.removeChild(modal);
        }
    });
}

// Direct function for saving experience changes
export async function saveExperienceDirectly(tagId, content, blockName) {
    try {
        const token = getAuthToken();
        if (!token) {
            console.error('Authentication required to save changes');
            return false;
        }

        const requestBody = {
            page_title: 'Home',
            tag_id: tagId,
            tag: tagId,
            content: content,
            order: 1,
            data_attribute: 'textarea'
        };

        if (blockName) {
            requestBody.block_name = blockName;
        }

        // Use apiPost directly
        const result = await apiPost(config.api.paths.content, requestBody);

        // Update hidden element in doctor card
        const doctorCard = document.querySelector(`[data-block-name="${blockName}"]`);
        if (doctorCard) {
            const hiddenExperience = doctorCard.querySelector(`.doctor-experience[data-content="${tagId}"]`);
            if (hiddenExperience) {
                hiddenExperience.setAttribute('data-original-content', content);
                hiddenExperience.classList.remove('content-modified');
            }
        }

        return true;
    } catch (error) {
        console.error(`Error saving experience for ${tagId}:`, error);
        return false;
    }
}

// Override publishTextChange method for experience work
export function patchPublishTextChange(originalPublishTextChange) {
    return async function publishTextChangePatch(tagId, change) {
        // Check if this is experience
        if (tagId.includes('-experience')) {
            // Check if element exists in DOM
            let element = document.querySelector(`[data-content="${tagId}"]`);

            // If element not in DOM (modal closed)
            if (!element) {
                // Extract block name from tagId (e.g., doctor-card-1)
                const blockName = tagId.split('-experience')[0];

                // Use cached data
                if (doctorExperienceCache[tagId]) {
                    // Use direct save function
                    const saved = await saveExperienceDirectly(tagId, doctorExperienceCache[tagId], blockName);

                    if (saved) {
                        return tagId;
                    } else {
                        throw new Error(`Failed to save for ${tagId}`);
                    }
                } else {
                    console.error(`No cached data for ${tagId}`);
                    // Remove change to not block other changes
                    removePendingChange('text', tagId);
                    return null;
                }
            } else {
                // If element found in DOM, use original function
                return originalPublishTextChange(tagId, change);
            }
        } else {
            // For regular text elements use original function
            return originalPublishTextChange(tagId, change);
        }
    };
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', initDoctorModals);

// If cards are added after carousel creation
document.addEventListener('carousel:initialized', function() {
    setTimeout(initDoctorModals, 500); // Add delay for certainty that all cards are created
});

// Export special function for initialization with publishTextChange patch
export function initDoctorModalsWithPublishPatch(publishTextChange) {
    // Create patch for publishTextChange function
    window.originalPublishTextChange = publishTextChange;
    window.patchedPublishTextChange = patchPublishTextChange(publishTextChange);

    // Perform usual initialization
    initDoctorModals();

    // Return patch for integration
    return window.patchedPublishTextChange;
}

// For creating doctor-card block in block system
export function createDoctorCard(block, parent) {
    const blockName = block.data_block_name;

    const card = document.createElement('div');
    card.className = 'doctor-card carousel-item';
    card.setAttribute('data-block-name', blockName);

    if (block.id && block.id !== 'undefined') {
        card.setAttribute('data-block-id', block.id);
    }

    if (block.order !== undefined && block.order !== null) {
        card.setAttribute('data-order', String(block.order));
    } else {
        card.setAttribute('data-order', '0');
    }

    card.style.position = 'relative';

    card.innerHTML = `
        <div class="doctor-image">
            <img src="" alt="" data-content="${blockName}-image" data-type="image">
        </div>
        <div class="doctor-info">
            <h3 class="doctor-name" data-content="${blockName}-name"></h3>
            <p class="doctor-description" data-content="${blockName}-description" data-type="textarea"></p>
            <!-- Hidden field for experience with necessary attributes -->
            <p class="doctor-experience hidden" data-content="${blockName}-experience" data-page="Home" data-type="textarea"></p>
        </div>
    `;

    parent.appendChild(card);
    return card;
}

export const doctorCardProps = {
    name: 'doctor-card',
    title: 'Doctor Card',
    description: 'Card with photo, name and doctor description',
    allowedParents: ['doctors-block'],
    contentFields: [
        {name: 'name', type: 'text', label: 'Doctor Name'},
        {name: 'description', type: 'textarea', label: 'Description'},
        {name: 'image', type: 'image', label: 'Doctor Photo'},
        {name: 'experience', type: 'textarea', label: 'Work Experience'} // Added field
    ],
    renderPreview: (data) => {
        return `
            <div class="block-preview doctor-card-preview">
                <div class="image-placeholder"></div>
                <h4>${data.name || 'Doctor Name'}</h4>
                <p>${data.description || 'Doctor description...'}</p>
                <p class="small">${data.experience ? 'Has work experience' : 'No work experience'}</p>
            </div>
        `;
    }
};

// Add CSS for hiding experience field in card and modal styles
const style = document.createElement('style');
style.textContent = `
    .doctor-card .doctor-experience.hidden {
        display: none;
    }
    
    /* Styles for editable content inside modal */
    .doctor-modal .editable-wrapper {
        position: relative;
        width: 100%;
    }
    
    .doctor-modal .edit-btn {
        position: absolute;
        top: 0;
        right: 0;
        opacity: 0.7;
        transition: opacity 0.3s ease;
    }
    
    .doctor-modal .edit-btn:hover {
        opacity: 1;
    }
    
    /* Modal appearance animation */
    .doctor-modal {
        animation: fadeIn 0.3s ease-in-out;
    }
    
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;
document.head.appendChild(style);